// A plan for Site Walks as a picture (Site Walks list #9, #16, #18): page 1
// of a drawing sheet's PDF rendered with pdf.js - or a picture file as it is
// - as a JPEG the longest side LONGEST pixels, sharp enough to zoom into on
// the map. Done in the browser, like the sheet previews (lib/sheet_preview.js),
// then sent to S3 as the plan's picture.
import * as pdfjsLib from "pdfjs-dist/build/pdf.mjs"

const LONGEST = 3200
const QUALITY = 0.85

// The pdf.js worker: from the page's own host element (the ChatLog panel
// carries data-pdf-worker-url on every project page), or the given URL.
function workerReady(fallback) {
  if (pdfjsLib.GlobalWorkerOptions.workerSrc) return true
  const host = document.querySelector("[data-pdf-worker-url], [data-drawings-specs-pdf-worker-url-value]")
  const url = fallback || (host && (host.dataset.pdfWorkerUrl || host.dataset.drawingsSpecsPdfWorkerUrlValue))
  if (url) pdfjsLib.GlobalWorkerOptions.workerSrc = url
  return Boolean(pdfjsLib.GlobalWorkerOptions.workerSrc)
}

// { blob, width, height } from { url } (a drawing sheet's file) or { file }.
export async function planPicture({ url, file, workerUrl } = {}) {
  const bytes = file ? await file.arrayBuffer() : await (await fetch(url)).arrayBuffer()
  const head = new TextDecoder().decode(new Uint8Array(bytes.slice(0, 5)))
  const canvas = head === "%PDF-" ? await renderPdf(bytes, workerUrl) : await renderPicture(bytes, file?.type)
  const blob = await new Promise((resolve, reject) => {
    canvas.toBlob((made) => (made ? resolve(made) : reject(new Error("picture"))), "image/jpeg", QUALITY)
  })
  return { blob, width: canvas.width, height: canvas.height }
}

async function renderPdf(bytes, workerUrl) {
  if (!workerReady(workerUrl)) throw new Error("pdf.js worker")
  const doc = await pdfjsLib.getDocument({ data: new Uint8Array(bytes) }).promise
  try {
    const page = await doc.getPage(1)
    const base = page.getViewport({ scale: 1 })
    const viewport = page.getViewport({ scale: LONGEST / Math.max(base.width, base.height) })
    const canvas = document.createElement("canvas")
    canvas.width = Math.ceil(viewport.width)
    canvas.height = Math.ceil(viewport.height)
    const ctx = canvas.getContext("2d")
    ctx.fillStyle = "#ffffff" // plans are drawn on white; a JPEG has no see-through
    ctx.fillRect(0, 0, canvas.width, canvas.height)
    await page.render({ canvasContext: ctx, viewport }).promise
    return canvas
  } finally {
    doc.destroy?.()
  }
}

async function renderPicture(bytes, type) {
  const bitmap = await createImageBitmap(new Blob([bytes], { type: type || "image/png" }))
  const scale = Math.min(1, LONGEST / Math.max(bitmap.width, bitmap.height))
  const canvas = document.createElement("canvas")
  canvas.width = Math.round(bitmap.width * scale)
  canvas.height = Math.round(bitmap.height * scale)
  const ctx = canvas.getContext("2d")
  ctx.fillStyle = "#ffffff"
  ctx.fillRect(0, 0, canvas.width, canvas.height)
  ctx.drawImage(bitmap, 0, 0, canvas.width, canvas.height)
  bitmap.close?.()
  return canvas
}
